const initialState = {
  qualityInspections: [],
  purchaseReturns: [],
  goodsReceipts: [],
  qiSyncedAt: null,
  returnsSyncedAt: null,
  grnsSyncedAt: null,
};

const CLIENT_UPSERT_TTL_MS = 120000;

const entityKey = (row) => {
  if (!row || typeof row !== "object") return "";
  for (const v of [row.POID, row.poid, row.id]) {
    if (v == null) continue;
    const s = String(v).trim();
    if (s && s !== "null" && s !== "undefined" && s !== "0") return s;
  }
  return "";
};

const upsertOne = (list, row) => {
  if (!row) return list || [];
  const key = entityKey(row);
  if (!key) return [row, ...(list || [])];
  const next = [...(list || [])];
  const idx = next.findIndex((x) => entityKey(x) === key);
  const stamped = { ...row, POID: key, id: key, _clientUpsertAt: Date.now() };
  if (idx >= 0) next[idx] = { ...next[idx], ...stamped };
  else next.unshift(stamped);
  return next;
};

/** Merge remote list with recent local upserts (same pattern as PO reducer). */
const mergeRemoteWithLocalUpserts = (localList, remoteRaw) => {
  const remote = Array.isArray(remoteRaw) ? remoteRaw : remoteRaw ? [remoteRaw] : [];
  const localByKey = new Map();
  (localList || []).forEach((row) => {
    const key = entityKey(row);
    if (key) localByKey.set(key, row);
  });

  const now = Date.now();
  const remoteKeys = new Set();
  const lineCount = (row) => {
    const lines = row?.lineItems || row?.goodsReceiptLines || row?.purchaseReturnLines;
    return Array.isArray(lines) ? lines.length : 0;
  };
  const mergedRemote = remote.map((r) => {
    const key = entityKey(r);
    if (key) remoteKeys.add(key);
    const local = key ? localByKey.get(key) : null;
    if (!local) return { ...r, POID: key || r.POID, id: key || r.id };
    // A list sync started before a create/stamp can return a stale row —
    // keep the just-upserted status/number instead of reverting to DRAFT.
    const localIsFresh =
      typeof local._clientUpsertAt === "number" &&
      now - local._clientUpsertAt < CLIENT_UPSERT_TTL_MS;
    const preferLocalLines = lineCount(local) > lineCount(r);
    return {
      ...local,
      ...r,
      POID: key,
      id: key,
      status: localIsFresh && local.status ? local.status : r.status || local.status,
      lineItems: preferLocalLines
        ? local.lineItems || local.goodsReceiptLines || local.purchaseReturnLines
        : r.lineItems ||
          r.goodsReceiptLines ||
          r.purchaseReturnLines ||
          local.lineItems ||
          local.goodsReceiptLines ||
          local.purchaseReturnLines ||
          [],
      _clientUpsertAt: local._clientUpsertAt || r._clientUpsertAt,
      ...(local.__detailFetched || r.__detailFetched
        ? { __detailFetched: true }
        : {}),
    };
  });

  const extras = [];
  localByKey.forEach((row, key) => {
    if (remoteKeys.has(key)) return;
    const at = row._clientUpsertAt;
    if (typeof at === "number" && now - at < CLIENT_UPSERT_TTL_MS) {
      extras.push(row);
    }
  });

  return extras.length ? [...extras, ...mergedRemote] : mergedRemote;
};

/**
 * Purchase-stock lists (QI / Returns / GRN) — mirror PO pattern:
 * keep in Redux, sync on demand, upsert on create success.
 */
const purchaseStockReducer = (state = initialState, action) => {
  switch (action.type) {
    case "GET_QUALITY_INSPECTIONS":
      return {
        ...state,
        qualityInspections: mergeRemoteWithLocalUpserts(
          state.qualityInspections,
          action.qualityInspections
        ),
        qiSyncedAt: Date.now(),
      };
    case "UPSERT_QUALITY_INSPECTION":
      return {
        ...state,
        qualityInspections: upsertOne(state.qualityInspections, action.qualityInspection),
      };
    case "GET_PURCHASE_RETURNS":
      return {
        ...state,
        purchaseReturns: mergeRemoteWithLocalUpserts(
          state.purchaseReturns,
          action.purchaseReturns
        ),
        returnsSyncedAt: Date.now(),
      };
    case "UPSERT_PURCHASE_RETURN":
      return {
        ...state,
        purchaseReturns: upsertOne(state.purchaseReturns, action.purchaseReturn),
      };
    case "GET_GOODS_RECEIPTS":
      return {
        ...state,
        goodsReceipts: mergeRemoteWithLocalUpserts(state.goodsReceipts, action.goodsReceipts),
        grnsSyncedAt: Date.now(),
      };
    case "UPSERT_GOODS_RECEIPT":
      return {
        ...state,
        goodsReceipts: upsertOne(state.goodsReceipts, action.goodsReceipt),
      };
    case "CLEAR_PURCHASE_STOCK_DATA":
      return { ...initialState };
    default:
      return state;
  }
};

export default purchaseStockReducer;
